// AddLlmDropdown — shared LLM picker popover (shad-pro).
//
// Extracted from CouncilView so other workspaces (GOD / Mesa de Deus) can reuse
// the exact same "+ Adicionar LLM" experience: portal to body (escapes
// overflow), auto-flip up/down, search, grouped-by-provider with status dots.
//
// Props:
//   open, onOpenChange(bool), disabled, count, groupedAvailable, search,
//   onSearchChange(str), onPick(modelId)

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, X } from 'lucide-react';
import ModelStatusDot from './ModelStatusDot.jsx';

export default function AddLlmDropdown({
  open,
  onOpenChange,
  disabled,
  count,
  groupedAvailable,
  search,
  onSearchChange,
  onPick,
  label = 'Adicionar LLM',
}) {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, bottom: null, left: 0, openUp: false });

  useLayoutEffect(() => {
    if (!open) return undefined;
    function updatePos() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popoverMaxHeight = 380;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const triggerCenter = (r.top + r.bottom) / 2;
      const openUp =
        triggerCenter > window.innerHeight * 0.55 ||
        (spaceBelow < popoverMaxHeight && spaceAbove > spaceBelow);
      setPos({
        top: openUp ? null : r.bottom + 6,
        bottom: openUp ? window.innerHeight - r.top + 6 : null,
        left: Math.min(r.left, window.innerWidth - 320 - 8),
        openUp,
      });
    }
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (!popoverRef.current || !triggerRef.current) return;
      if (popoverRef.current.contains(e.target) || triggerRef.current.contains(e.target)) return;
      onOpenChange(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  function pick(id) {
    onPick(id);
    onOpenChange(false);
    onSearchChange('');
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={count === 0 ? 'Sem modelos disponíveis pra adicionar' : `${label} (${count} disponíveis)`}
        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-apolo-borderSubtle bg-transparent px-2.5 py-1 text-[11px] text-apolo-textTertiary transition hover:border-apolo-accent hover:bg-apolo-surface2/40 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:border-apolo-accent focus-visible:ring-2 focus-visible:ring-apolo-accent/30"
      >
        <Plus size={11} aria-hidden="true" strokeWidth={2.25} />
        {label}
        {count > 0 && (
          <span className="rounded-full bg-apolo-surface2 px-1.5 py-px text-[9px] font-medium text-apolo-textSecondary">
            {count}
          </span>
        )}
      </button>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                ref={popoverRef}
                role="listbox"
                aria-label={label}
                initial={{ opacity: 0, y: pos.openUp ? 6 : -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: pos.openUp ? 6 : -6, scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.6 }}
                className="fixed z-[100] flex w-[320px] flex-col overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 shadow-[0_24px_64px_-12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)] backdrop-blur-md"
                style={{
                  transformOrigin: pos.openUp ? 'bottom left' : 'top left',
                  top: pos.top ?? undefined,
                  bottom: pos.bottom ?? undefined,
                  left: pos.left,
                }}
              >
                <div className="flex items-center gap-2 border-b border-apolo-borderSubtle bg-apolo-surface2/40 px-3 py-2">
                  <Search size={11} aria-hidden="true" strokeWidth={2.25} className="shrink-0 text-apolo-textTertiary" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Buscar modelo ou provider…"
                    autoFocus
                    className="flex-1 bg-transparent text-[11.5px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => onSearchChange('')}
                      aria-label="Limpar busca"
                      className="text-apolo-textTertiary transition hover:text-apolo-textPrimary"
                    >
                      <X size={10} aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="max-h-[320px] overflow-y-auto p-1">
                  {groupedAvailable.length === 0 ? (
                    <div className="px-3 py-6 text-center text-[11px] text-apolo-textTertiary">
                      {search ? 'Nenhum modelo encontrado pra essa busca.' : 'Todos os modelos já estão na mesa.'}
                    </div>
                  ) : (
                    groupedAvailable.map(({ provider, models }) => (
                      <div key={provider} className="mb-1 last:mb-0">
                        <div className="sticky top-0 z-[1] flex items-center gap-1.5 bg-apolo-surface1/95 px-2 pb-1 pt-2 text-[9.5px] font-semibold uppercase tracking-[0.08em] text-apolo-textTertiary backdrop-blur-sm">
                          {provider}
                          <span className="rounded-full bg-apolo-surface3 px-1.5 text-[9px] font-medium tabular-nums text-apolo-textSecondary ring-1 ring-inset ring-apolo-borderSubtle">
                            {models.length}
                          </span>
                        </div>
                        {models.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => pick(m.id)}
                            className="group/item flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-apolo-surface2"
                          >
                            <ModelStatusDot connected={m.connected} size={6} />
                            <code className="flex-1 truncate font-mono text-[11px] text-apolo-textPrimary">{m.id}</code>
                            {m.source === 'adapter' && (
                              <span className="shrink-0 rounded-sm border border-violet-500/30 bg-violet-500/10 px-1 py-px text-[9px] font-medium uppercase tracking-wider text-violet-300">
                                adapter
                              </span>
                            )}
                            <Plus size={10} aria-hidden="true" strokeWidth={2.25} className="shrink-0 text-apolo-textTertiary opacity-0 transition group-hover/item:opacity-100" />
                          </button>
                        ))}
                      </div>
                    ))
                  )}
                </div>

                <div className="border-t border-apolo-borderSubtle bg-apolo-surface2/40 px-3 py-1.5 text-[10px] text-apolo-textTertiary">
                  <kbd className="rounded bg-apolo-surface3 px-1 text-[9px]">Esc</kbd> fechar
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
